import { Component, OnInit } from '@angular/core';
import {
  fadeSlide,
  InOutAnimation,
} from 'src/app/shared/animations/animations';
import { AccountSetupHelperService } from '../../account-setup-helper.service';
import { ColorPalette } from './color-palette-selector/color-palette-selector.component';

type StepKey = 'colorPalette' | 'logo';

export enum StepStatusEnum {
  done = 'done',
  inProgress = 'inProgress',
  notStarted = 'notStarted',
}
interface StepConfig {
  key: StepKey;
  label: string;
  status: StepStatusEnum;
}

@Component({
  selector: 'app-hub-branding-step',
  templateUrl: './hub-branding-step.component.html',
  styleUrls: ['./hub-branding-step.component.css'],
  animations: [InOutAnimation, fadeSlide],
})
export class HubBrandingStepComponent implements OnInit {
  manualNavigationLocked = false;
  readonly stepStatusEnum = StepStatusEnum;
  /** Ordered step definitions */
  readonly steps: StepConfig[] = [
    {
      key: 'colorPalette',
      label: 'Color Palette',
      status: StepStatusEnum.notStarted,
    },
    { key: 'logo', label: 'Logo', status: StepStatusEnum.notStarted },
  ];

  currentStep = 0; // active step index
  isGenerating = false; // controls AI loader state

  /** Counters to re-trigger AI generation */
  triggerCounter: Record<StepKey, number> = {
    colorPalette: 0,
    logo: 0,
  };

  constructor(public accountSetupHelperService: AccountSetupHelperService) {}

  ngOnInit(): void {
    const aiState = this.accountSetupHelperService.wizardData.hubInput.aiState;
    const hubBranding = this.accountSetupHelperService.wizardData.hubInput.hubBranding;

    // Ensure aiData is initialized
    if (!aiState.aiData) {
      aiState.aiData = { idea: '', audience: '', goals: '' };
    }

    // Default to placeholder if no mode is set
    if (!hubBranding.logoMode || hubBranding.logoMode === 'none') {
      hubBranding.logoMode = 'placeholder';
    }

    // Generate placeholder if in placeholder mode and not yet generated
    if (hubBranding.logoMode === 'placeholder' && !hubBranding.placeHolderLogo) {
      const generated = this.accountSetupHelperService.generatePlaceholderLogo();
      if (generated) hubBranding.placeHolderLogo = generated;
    }

    // Initialize step statuses based on existing data
    if (hubBranding.colorPalette) {
      this.setStepStatus('colorPalette', StepStatusEnum.done);
    }
    // Placeholder mode is always "done" (user never needs to explicitly select anything)
    if (hubBranding.logoMode === 'placeholder' || hubBranding.logoMode === 'upload') {
      this.setStepStatus('logo', StepStatusEnum.done);
    }

    // Initial loader message + prompts
    this.accountSetupHelperService.setAiLoaderMessage('Thinking...');
  }

  onLogoModeChange(mode: 'none' | 'placeholder' | 'upload') {
    if (mode === 'placeholder' || mode === 'upload') {
      this.setStepStatus('logo', StepStatusEnum.done);
    }
    this.accountSetupHelperService.scheduleSaveProgress();
  }

  onNeedToChangePalette() {
    this.prevStep();
  }
  onSelectPalette(palette: ColorPalette) {
    this.accountSetupHelperService.wizardData.hubInput.hubBranding.colorPalette = palette;
    this.setStepStatus('colorPalette', StepStatusEnum.done);
    // Regenerate placeholder with new palette (only if placeholder mode is active)
    if (this.accountSetupHelperService.wizardData.hubInput.hubBranding.logoMode === 'placeholder') {
      const generated = this.accountSetupHelperService.generatePlaceholderLogo(undefined, palette);
      if (generated) this.accountSetupHelperService.wizardData.hubInput.hubBranding.placeHolderLogo = generated;
    }
    this.accountSetupHelperService.scheduleSaveProgress();
    this.nextStep();
  }

  setStepStatus(step: StepKey, status: StepStatusEnum): void {
    const stepIndex = this.steps.findIndex((s) => s.key === step);
    if (stepIndex !== -1) {
      this.steps[stepIndex].status = status;
    }
  }

  /** Called after a step finishes AI generation */
  async onStepDone(): Promise<void> {
    // Small gap so user can read results before auto-advancing
    const isThereNextStep = this.isThereNextStep;

    this.setStepStatus(this.steps[this.currentStep].key, StepStatusEnum.done);
    if (!isThereNextStep) {
      this.accountSetupHelperService.setAiLoaderMessage('Done!', 4000);
      this.accountSetupHelperService.setAiLoaderState('idle');
      this.manualNavigationLocked = false;
    } else {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      await this.nextStep();
    }
  }

  /** Start AI generation for the given step */

  // Navigate To Specific Step
  navigateToStep(step: number, force = false): void {
    if (this.isGenerating) return;
    if (this.manualNavigationLocked && !force) return;

    // Can only navigate to completed steps or earlier steps
    const targetStep = this.steps[step];
    if (!targetStep) return;

    // Allow navigation to completed steps or going backward
    if (
      step < this.currentStep ||
      targetStep.status === this.stepStatusEnum.done
    ) {
      this.currentStep = step;
    }
  }

  canNavigateToStep(stepIndex: number): boolean {
    if (this.isGenerating) return false;
    if (this.manualNavigationLocked) return false;

    const targetStep = this.steps[stepIndex];
    if (!targetStep) return false;

    // Can navigate to completed steps or earlier steps
    return (
      stepIndex < this.currentStep ||
      stepIndex === this.currentStep ||
      targetStep.status === this.stepStatusEnum.done
    );
  }

  // Is There Next Step?
  get isThereNextStep() {
    return !(this.currentStep >= this.steps.length - 1);
  }
  /** Go forward in steps */
  async nextStep(): Promise<void> {
    if (this.currentStep >= this.steps.length - 1) return;

    this.isGenerating = true;

    // Placeholder async delay (replace with actual AI logic if needed)
    // await new Promise(resolve => setTimeout(resolve, 2000));

    this.isGenerating = false;
    this.currentStep++;

    const aiData =
      this.accountSetupHelperService.wizardData.hubInput.aiState.aiData;
    const nextStepKey = this.steps[this.currentStep].key;
  }

  /** Go backward in steps */
  prevStep(): void {
    if (this.currentStep > 0 && !this.isGenerating) {
      this.currentStep--;
    }
  }

  /** Increment counter to trigger AI generation */
  private triggerGenerate(target: StepKey): void {
    this.triggerCounter[target]++;
  }

  /** Build prompts dynamically from hub idea */
}
